/**
 * Constants and helpers for talking to the embedded drawio iframe.
 *
 * drawio's embed mode exposes a postMessage-based protocol (documented at
 * drawio.com/doc/faq/embed-mode) which this module wraps. Only the subset we
 * actually use is declared here.
 */

/** Self-hosted drawio served from the same CloudFront distribution under /drawio/,
 * eliminating cross-site cookie issues with drawio.corp.amazon.com. */
export const DRAWIO_ORIGIN = window.location.origin;

/**
 * URL parameters that put drawio into embed mode with the splash screen and
 * default save/exit buttons suppressed. See:
 *   - embed=1: embed mode on
 *   - proto=json: use JSON postMessage protocol
 *   - spin=1: show spinner until the parent sends load
 *   - ui=atlas: modern UI theme (dark also available)
 *   - libraries=1: enable shape libraries
 *   - configure=0: skip the configure-editor exchange
 *   - saveAndExit=0, noSaveBtn=1, noExitBtn=1: parent controls saves;
 *     drawio never shows its own save/exit UI since we persist via autosave.
 */
const DRAWIO_EMBED_PARAMS = new URLSearchParams({
  embed: "1",
  proto: "json",
  spin: "1",
  ui: "atlas",
  libraries: "1",
  configure: "0",
  saveAndExit: "0",
  noSaveBtn: "1",
  noExitBtn: "1",
});

export const DRAWIO_EMBED_URL = `${DRAWIO_ORIGIN}/drawio/index.html?${DRAWIO_EMBED_PARAMS.toString()}`;

/**
 * Minimal empty mxGraphModel document. Used as the initial payload when no
 * prior saved state exists.
 */
export const DRAWIO_BLANK_XML =
  '<mxGraphModel dx="800" dy="600" grid="1" gridSize="10" guides="1" ' +
  'tooltips="1" connect="1" arrows="1" fold="1" page="1" pageScale="1" ' +
  'pageWidth="850" pageHeight="1100" math="0" shadow="0">' +
  "<root>" +
  '<mxCell id="0"/>' +
  '<mxCell id="1" parent="0"/>' +
  "</root>" +
  "</mxGraphModel>";

/** Event types drawio sends to the parent via postMessage. */
export enum DrawioEvent {
  INIT = "init",
  AUTOSAVE = "autosave",
  SAVE = "save",
  EXPORT = "export",
}

/** Action types the parent sends to drawio via postMessage. */
export enum DrawioAction {
  LOAD = "load",
  EXPORT = "export",
}

/** Shape of every parsed drawio -> parent message we care about. */
export interface DrawioInboundMessage {
  event: DrawioEvent;
  xml?: string;
  /** Additional fields (pageVisible, bounds, data, etc.) — preserved for
   * forwarding but typed loosely since we don't consume them. */
  [key: string]: unknown;
}

/** Shape of a parent -> drawio message. */
export interface DrawioOutboundMessage {
  action: DrawioAction;
  xml?: string;
  /** Autosave enable flag (1/0) for load actions. */
  autosave?: 0 | 1;
  /** Export format (e.g., "xml", "xmlsvg"). */
  format?: string;
  /** Target output width in pixels for raster exports. */
  width?: number;
}

/**
 * Parses a postMessage payload from drawio. Returns null if the message is
 * from a different origin or does not look like a drawio message.
 */
export function parseDrawioMessage(
  event: MessageEvent,
): DrawioInboundMessage | null {
  if (event.origin !== DRAWIO_ORIGIN) {
    return null;
  }
  if (typeof event.data !== "string") {
    return null;
  }
  try {
    const parsed = JSON.parse(event.data) as unknown;
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof (parsed as { event?: unknown }).event === "string"
    ) {
      return parsed as DrawioInboundMessage;
    }
  } catch {
    // Not JSON — drawio sometimes emits non-JSON frames (e.g. ready ping).
    // Ignore.
  }
  return null;
}

/**
 * Serializes and posts a message to the drawio iframe.
 */
export function sendDrawioMessage(
  iframe: HTMLIFrameElement,
  message: DrawioOutboundMessage,
): void {
  iframe.contentWindow?.postMessage(JSON.stringify(message), DRAWIO_ORIGIN);
}
